import { useState } from "react";
import { SportType } from "../types/training";
import { SPORT_LABELS } from "./plannerProfile";
import { listBankSessions, Focus, RaceDistance } from "./workoutBank";
import {
  CustomSession, loadCustomSessions, addCustomSession, removeCustomSession,
  loadFavoriteIds, toggleFavoriteId,
} from "./intervalBank";
import "./IntervalBankPanel.scss";

/**
 * The planner's interval-session bank, surfaced so the athlete can SEE every
 * session pickIntervalSession() chooses between, STAR favorites (preferred
 * within their focus tier) and ADD their own when one is missing. Favorites +
 * custom sessions feed back into generation via generateTrainingPlan opts.
 * Changes take effect the next time the plan is generated.
 */

interface Row {
  id: string;
  name: string;
  focus: Focus;
  sports: SportType[];
  distances: RaceDistance[];
  minCTL: number;
  example: string;
  rationale: string;
  custom: boolean;
}

const FOCUS_ORDER: Focus[] = ["aerobic", "threshold", "vo2max", "race"];
const FOCUS_LABEL: Record<Focus, string> = {
  aerobic: "Aerob bas",
  threshold: "Tröskel",
  vo2max: "VO2max",
  race: "Tävlingsspecifik",
};

const BANK_SPORTS: { value: SportType; label: string }[] = [
  { value: "Run", label: "Löpning" },
  { value: "Ride", label: "Cykling" },
  { value: "NordicSki", label: "Skidor" },
  { value: "RollerSki", label: "Rullskidor" },
];

const ALL_DIST: RaceDistance[] = ["5k", "10k", "half", "marathon", "ultra"];
const DIST_LABEL: Record<string, string> = {
  "5k": "5k", "10k": "10k", half: "Halv", marathon: "Maraton", ultra: "Ultra", unknown: "Övrigt",
};

function distSummary(d: RaceDistance[]): string {
  const real = d.filter(x => x !== "unknown");
  return real.length >= 5 ? "alla distanser" : real.map(x => DIST_LABEL[x]).join(", ");
}

function buildRows(custom: CustomSession[]): Row[] {
  const builtIn: Row[] = listBankSessions().map(s => ({
    id: s.id, name: s.name, focus: s.focus, sports: s.sports,
    distances: s.distances, minCTL: s.minCTL, example: s.example,
    rationale: s.rationale, custom: false,
  }));
  const customRows: Row[] = custom.map(c => ({
    id: c.id, name: c.name, focus: c.focus, sports:
      c.sport === "NordicSki" || c.sport === "RollerSki" ? ["NordicSki", "RollerSki"] : [c.sport],
    distances: c.distances, minCTL: c.minCTL, example: c.body,
    rationale: "Eget pass.", custom: true,
  }));
  return [...builtIn, ...customRows];
}

export function IntervalBankPanel() {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState<CustomSession[]>(() => loadCustomSessions());
  const [favs, setFavs] = useState<string[]>(() => loadFavoriteIds());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);

  // New-session form state
  const [name, setName] = useState("");
  const [sport, setSport] = useState<SportType>("Run");
  const [focus, setFocus] = useState<Focus>("threshold");
  const [minCTL, setMinCTL] = useState("0");
  const [dists, setDists] = useState<Set<RaceDistance>>(new Set(ALL_DIST));
  const [body, setBody] = useState("");

  const rows = buildRows(custom);
  const favSet = new Set(favs);

  function toggleDist(d: RaceDistance) {
    setDists(prev => {
      const next = new Set(prev);
      next.has(d) ? next.delete(d) : next.add(d);
      return next;
    });
  }

  function saveNew() {
    if (!name.trim() || !body.trim()) return;
    // Always keep "unknown" eligible so the session still applies to races whose
    // distance the planner couldn't classify.
    const distances = [...Array.from(dists), "unknown" as RaceDistance];
    setCustom(addCustomSession({
      name: name.trim(), sport, focus,
      minCTL: parseInt(minCTL, 10) || 0,
      distances, body: body.trim(),
    }));
    setName(""); setBody(""); setMinCTL("0"); setSport("Run"); setFocus("threshold");
    setDists(new Set(ALL_DIST)); setShowNew(false);
  }

  return (
    <section className="ibp">
      <button className={`ibp__toggle${open ? " ibp__toggle--open" : ""}`} onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="ibp__toggle-caret">{open ? "▾" : "▸"}</span>
        Intervallbank
        <span className="ibp__toggle-hint">{rows.length} pass{favs.length ? ` · ${favs.length} ★` : ""}</span>
      </button>

      {open && (
        <div className="ibp__body">
          <p className="ibp__intro">
            Alla kvalitetspass planeraren väljer mellan. Stjärnmärk favoriter — då föredras de när
            flera passar i samma fas. Saknas något, lägg till ett eget. Ändringar slår igenom nästa
            gång planen genereras.
          </p>

          {FOCUS_ORDER.map(f => {
            const group = rows.filter(r => r.focus === f)
              .sort((a, b) => Number(favSet.has(b.id)) - Number(favSet.has(a.id)) || a.name.localeCompare(b.name, "sv"));
            if (group.length === 0) return null;
            return (
              <div key={f} className="ibp__group">
                <div className={`ibp__group-head ibp__group-head--${f}`}>{FOCUS_LABEL[f]}</div>
                {group.map(r => (
                  <div key={r.id} className="ibp__row">
                    <div className="ibp__row-main">
                      <button
                        className={`ibp__star${favSet.has(r.id) ? " ibp__star--on" : ""}`}
                        title={favSet.has(r.id) ? "Ta bort favorit" : "Markera som favorit"}
                        aria-pressed={favSet.has(r.id)}
                        onClick={() => setFavs(toggleFavoriteId(r.id))}
                      >{favSet.has(r.id) ? "★" : "☆"}</button>
                      <button className="ibp__row-toggle" onClick={() => setExpanded(e => e === r.id ? null : r.id)}>
                        <span className="ibp__name">{r.name}{r.custom && <span className="ibp__tag-custom">eget</span>}</span>
                        <span className="ibp__meta">
                          {r.sports.map(s => SPORT_LABELS[s]).join("/")} · {distSummary(r.distances)}{r.minCTL > 0 ? ` · CTL ≥ ${r.minCTL}` : ""}
                        </span>
                      </button>
                      {r.custom && (
                        <button className="ibp__del" title="Ta bort" onClick={() => setCustom(removeCustomSession(r.id))}>✕</button>
                      )}
                    </div>
                    {expanded === r.id && (
                      <div className="ibp__detail">
                        <pre className="ibp__example">{r.example}</pre>
                        {r.rationale && <p className="ibp__rationale">{r.rationale}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}

          {showNew ? (
            <div className="ibp__form">
              <input className="ibp__input" placeholder="Namn (t.ex. Mina tröskelblock)" value={name} onChange={e => setName(e.target.value)} />
              <div className="ibp__form-row">
                <select className="ibp__input" value={sport} onChange={e => setSport(e.target.value as SportType)}>
                  {BANK_SPORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <select className="ibp__input" value={focus} onChange={e => setFocus(e.target.value as Focus)}>
                  {FOCUS_ORDER.map(f => <option key={f} value={f}>{FOCUS_LABEL[f]}</option>)}
                </select>
                <input className="ibp__input ibp__input--ctl" type="number" min={0} placeholder="min CTL" value={minCTL} onChange={e => setMinCTL(e.target.value)} />
              </div>
              <div className="ibp__dist-chips">
                {ALL_DIST.map(d => (
                  <button
                    key={d}
                    className={`ibp__chip${dists.has(d) ? " ibp__chip--on" : ""}`}
                    onClick={() => toggleDist(d)}
                  >{DIST_LABEL[d]}</button>
                ))}
              </div>
              <textarea
                className="ibp__input ibp__textarea"
                placeholder={"Passet i intervals.icu-format, t.ex.\n15min Z2 HR\n4x\n- 8min Z4 HR\n- 2min Z2 HR\n10min Z1 HR"}
                value={body}
                onChange={e => setBody(e.target.value)}
                rows={6}
              />
              <div className="ibp__form-actions">
                <button className="ibp__save" disabled={!name.trim() || !body.trim()} onClick={saveNew}>Spara pass</button>
                <button className="ibp__cancel" onClick={() => setShowNew(false)}>Avbryt</button>
              </div>
            </div>
          ) : (
            <button className="ibp__new-toggle" onClick={() => setShowNew(true)}>+ Lägg till eget pass</button>
          )}
        </div>
      )}
    </section>
  );
}
